import querystring from 'querystring';
import { path } from 'ramda';

import { bookingStatus as bookingStatusTypes } from 'types/booking';
import { CLASSES_OF_SERVICE_BY_TYPE, WC_COMP } from 'constants/classOfService';
import findCollectionByPassengerId from 'utils/findCollectionByPassengerId';
import { getOldClassOfService } from 'utils/classOfService';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { ER_OFFLINE_FEE, OFFLINE_FEE } from 'types/fees';

import { splitLegsByDirection } from '../components/ui/ReviewBookingSummary/SegmentSummary/SegmentSummary.utils';

const mapToPassengersData = ({ passenger: { id, firstName, lastName, type }, price }, index, { seats }) => {
  const seat = findCollectionByPassengerId(id, seats);

  return {
    key: id,
    hash: index + 1,
    firstName,
    lastName,
    passengerType: type,
    coach: seat && seat.coach,
    seat: seat && seat.number,
    totalPrice: price
  };
};

const getBookingItemRefsFromParams = (search) => {
  const params = querystring.parse(search.substr(1));
  let bookingItems = [];
  if (params.bookingItems) {
    bookingItems = params.bookingItems.split(',');
  }

  return bookingItems;
};

const exchangeableBookingItems = (bookingItems, bookingDisplayStatus, bookingItemsRefs, overrideExchangeRules) =>
  bookingItems.filter(({ canBeRebooked, provisional, itemRef }) => {
    const isBookable =
      (canBeRebooked === true || overrideExchangeRules) && (provisional === false || bookingDisplayStatus === bookingStatusTypes.CONFIRMED);

    return isBookable && bookingItemsRefs.includes(itemRef);
  });

const getBookingItems = (leg) => (leg && leg.bookingItems) || [];

const getJourneyWithFilteredBookingItems = ({ outbound, inbound, displayStatus }, bookingItemRefs) => {
  const outboundExchangeable = exchangeableBookingItems(getBookingItems(outbound), displayStatus, bookingItemRefs);
  const inboundExchangeable = exchangeableBookingItems(getBookingItems(inbound), displayStatus, bookingItemRefs);

  if (outboundExchangeable.length) {
    return {
      ...outbound,
      bookingItems: outboundExchangeable,
      direction: 'outbound'
    };
  }
  if (inboundExchangeable.length) {
    return {
      ...inbound,
      bookingItems: inboundExchangeable,
      direction: 'inbound'
    };
  }

  return null;
};

const getJourneysWithFilteredBookingItems = ({ outbound, inbound, displayStatus }, bookingItemRefs, overrideExchangeRules) => {
  const outboundExchangeable = exchangeableBookingItems(getBookingItems(outbound), displayStatus, bookingItemRefs, overrideExchangeRules);

  const inboundExchangeable = exchangeableBookingItems(getBookingItems(inbound), displayStatus, bookingItemRefs, overrideExchangeRules);

  return {
    outbound: {
      ...outbound,
      bookingItems: outboundExchangeable,
      direction: 'outbound'
    },
    inbound: {
      ...inbound,
      bookingItems: inboundExchangeable,
      direction: 'inbound'
    }
  };
};

const getPassengerCountByType = (bookingItems, passengerType) => {
  const matchingBookingItems = bookingItems.filter((bookingItem) => bookingItem.passenger.type === passengerType);

  return [...new Set(matchingBookingItems.map((matchingBookingItem) => matchingBookingItem.passenger.id))].length;
};

const findStationName = (stations, uic) => {
  const { name } = stations.find((s) => s.uic === uic);

  return name;
};

const getOfflineFees = ({
  outbound,
  inbound,
  bookingTypeSelected,
  isSubscriptionTicket = false,
  isThalysUser = false,
  hasSpecialLoyaltyTier = false,
  hasSpecialAssistance = false,
  isPackageBooking = false
}) => {
  if (
    bookingTypeSelected === 'withDisability' ||
    hasSpecialAssistance ||
    isSubscriptionTicket ||
    hasSpecialLoyaltyTier ||
    isPackageBooking
  ) {
    return null;
  }

  const hasBPorWheelchair = [outbound, inbound].some((seg) => {
    const classOfService = path(['classOfService', 'code'], seg);

    return [...CLASSES_OF_SERVICE_BY_TYPE.PREMIER, WC_COMP].includes(classOfService);
  });

  if (hasBPorWheelchair) return null;

  const fees = path(['fees'], outbound || inbound);
  return isThalysUser ? fees?.find((fee) => fee.code === ER_OFFLINE_FEE) : fees?.find((fee) => fee.code === OFFLINE_FEE);
};

const isTwoWayExchange = ({ outboundDate, inboundDate }) => [outboundDate, inboundDate].filter(Boolean).length === 2;

const isOneWayExchange = ({ outboundDate, inboundDate }) => [outboundDate, inboundDate].filter(Boolean).length === 1;

const hasInbound = (booking) => {
  const [, inboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  return inboundCreationLegs.length > 0;
};

const hasOutbound = (booking) => {
  const [outboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  return outboundCreationLegs.length > 0;
};

const getJourneyType = (booking) => {
  const [outboundCreationLegs, inboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  if (outboundCreationLegs.length > 0 && inboundCreationLegs.length > 0) {
    return 'Return';
  }

  return 'Single';
};

const getOriginUIC = (booking) => {
  const [outboundCreationLegs, inboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  const lookupList = outboundCreationLegs.length > 0 ? outboundCreationLegs : inboundCreationLegs;

  const sortedByDeparture = [...lookupList].sort((a, b) => new Date(a.timing.isoDate) - new Date(b.timing.isoDate));

  return sortedByDeparture[0].origin.uic;
};

const getDestinationUIC = (booking) => {
  const [outboundCreationLegs, inboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  const lookupList = outboundCreationLegs.length > 0 ? outboundCreationLegs : inboundCreationLegs;

  const sortedByDepartureDesc = [...lookupList].sort((a, b) => new Date(b.timing.isoDate) - new Date(a.timing.isoDate));

  return sortedByDepartureDesc[0].destination.uic;
};

const getDepartureDateTime = (booking) => {
  const [outboundCreationLegs, inboundCreationLegs] = splitLegsByDirection(booking.pendingCreationLegs || []);

  return !hasOutbound(booking) ? inboundCreationLegs[0].timing.isoDate : outboundCreationLegs[0].timing.isoDate;
};

const getRedeemTicketsData = (booking) => {
  const tickets = [];

  booking.pendingCreationLegs.forEach((leg, pIndex) => {
    const oldLeg = booking.pendingCancellationLegs[pIndex];

    const serviceCode = leg.serviceType.code;

    leg.bookingItems.forEach((bookingItem, index) => {
      tickets.push({
        tcn: oldLeg.bookingItems[index].ticketNumber,
        originalDepartureDateTime: oldLeg.timing.isoDate,
        newDepartureDateTime: leg.timing.isoDate,
        classOfAccommodation: getOldClassOfService(leg.classOfService.code),
        classOfService: leg.classOfService.code,
        bucketCode: bookingItem.tariffDetails.bucketCode,
        ...(serviceCode === serviceTypeCodes.EUROSTAR && { tariffCode: bookingItem.tariffDetails.tariffCode })
      });
    });
  });

  return tickets;
};

const hasCashPayments = (booking) => booking.cashPayments?.length > 0;

export {
  findStationName,
  getBookingItemRefsFromParams,
  exchangeableBookingItems,
  getBookingItems,
  getJourneyWithFilteredBookingItems,
  getJourneysWithFilteredBookingItems,
  mapToPassengersData,
  getPassengerCountByType,
  isOneWayExchange,
  isTwoWayExchange,
  getOfflineFees,
  hasInbound,
  hasOutbound,
  getJourneyType,
  getOriginUIC,
  getDestinationUIC,
  getDepartureDateTime,
  getRedeemTicketsData,
  hasCashPayments
};
